import {useLocation, useParams, useSearchParams} from "react-router-dom";
import TicketDetails from "../../classes/api/TicketDetails";
import {useQuery} from "react-query";
import {getTicketDetails} from "../../api/ticket";
import Loading from "../../common/components/Loading";
import Ticket from "./ticket-detail-index/Ticket";
import NotFoundRedirect from "../../common/components/NotFoundRedirect";
import {isNotFoundError} from "../../common/tools/httpError";

/**
 * Represents properties for administrative components or configurations.
 */
interface AdministrativeProps {
    activeKey: string;
}

/**
 * Represents the state of a location with customer and ticket information.
 *
 * @interface locationState
 *
 * @property {string} customerId - The unique identifier for the customer.
 * @property {string} ticketId - The unique identifier for the ticket associated with the location.
 */
interface locationState {
    customerId: string;
    ticketId: string;
}

/**
 * TicketDetailIndex is a functional component responsible for rendering the details of a specific ticket.
 * It fetches the ticket details based on the provided ticket ID and customer ID from the location state using a query.
 * The component displays a loading indicator while the ticket details are being fetched and displays the ticket details once retrieved.
 *
 * @param {AdministrativeProps} props - The properties required for the component, including the activeKey to manage the active tab or key state in the parent component.
 * @returns {JSX.Element} A JSX element rendering a loading indicator or the ticket details depending on the loading state.
 */
const TicketDetailIndex = ({ activeKey }: AdministrativeProps): JSX.Element => {
    const location = useLocation();
    const { ticketId } = useParams<{ ticketId: string }>();
    const [searchParams] = useSearchParams();
    
    const state = location.state as locationState | null;
    const ticketIdFinal = ticketId || state?.ticketId || '';
    const customerId = searchParams.get('customerId') || state?.customerId || '';
    
    const {isLoading: isTicketLoading, data: ticket, refetch: refetchTicket, error: ticketError} = useQuery<TicketDetails, Error>(
        ['ticket' + ticketIdFinal],
        () => getTicketDetails(customerId, ticketIdFinal),
        {enabled: !!ticketIdFinal && !!customerId}
    );

    if (isNotFoundError(ticketError)) {
        return <NotFoundRedirect />;
    }

    return (
        <>
            {isTicketLoading && <Loading />}
            {!isTicketLoading && <Ticket ticket={ticket} refetchTicket={refetchTicket} />}
        </>
    );
};

export default TicketDetailIndex;
